// ApprovalPanel — one pending Approval (FD §21.2): what the agent wants to
// do (rendered per action kind, not as raw JSON), why, the context it
// belongs to, and the decision: approve, reject, or reply with an
// instruction. Used by the Approvals inbox and inline on the Run detail page
// when a run is `awaiting_approval`.
//
// Per FD §21.2 the approval is bound to a hash of the exact proposed
// action ("Bij wijziging van de actie vervalt de approval"); every decision
// submitted from here carries the `actionHash` currently displayed so the
// API can reject a stale decision (see approvals/respond.ts ->
// ApprovalsService.recordDecision, which re-verifies authoritatively). The
// local re-hash below is a UX nicety, not a security boundary.

import { LiveDuration } from './live-duration'
import { Markdown } from './markdown'
import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import type { AgentQuestionAction, AgentQuestionItem, Approval, ApprovalAction, TaskRunStep, WorkflowGateContext } from '@goblic/shared'
import { isApprovalActionUnchanged } from '@goblic/shared'
import { StatusBadge } from './status-badge'
import { StepChain } from './step-chain'
import { formatDuration, formatMoney, formatRelative, humanize } from '../lib/format'
import { MomentArt } from './goblin-art'
import { TaskReportLink } from './reports/run-report'

/** One sub-question's answer, inside `AgentQuestionAnswer.answers` (a multi-question approval). */
export interface AgentQuestionAnswerItem {
  id?: string
  index?: number
  answer?: string
  choices?: string[]
  other?: string
}

/**
 * What `onAnswer` sends for an `agent_question` reply: `answer` for a
 * `'text'`/`'yes_no'` question, `choices`/`other` for a `'single_choice'`/
 * `'multiple_choice'` one, or `answers` (one entry per sub-question) for a
 * multi-question approval (`AgentQuestionAction.questions`) — mirrors
 * `approvalsApi.answer`'s body (api-client.ts), which the API validates
 * against the question's stored shape (approvals.service.ts's
 * `recordDecision`).
 */
export interface AgentQuestionAnswer {
  answer?: string
  choices?: string[]
  other?: string
  answers?: AgentQuestionAnswerItem[]
  /** Optional extra context for the agent, sent with any answer. */
  comment?: string
}

export interface ApprovalPanelProps {
  approval: Approval
  isSubmitting?: boolean
  /** Resolves ids to names; falls back to the raw id. */
  agentName?: (id: string) => string
  projectName?: (id: string) => string
  /** Title of the task a `workflow_gate` approval belongs to. */
  taskName?: (id: string) => string
  /** Hide the "Open run" link when already on the run page. */
  showRunLink?: boolean
  onApprove: (
    approvalId: string,
    actionHash: string,
    note?: string
  ) => Promise<unknown> | void
  onReject: (
    approvalId: string,
    actionHash: string,
    note?: string
  ) => Promise<unknown> | void
  onInstruct: (
    approvalId: string,
    actionHash: string,
    instruction: string
  ) => Promise<unknown> | void
  /** Replies to an `agent_question` approval (the `ask_human` run-tool). Required when such an approval may be shown. */
  onAnswer?: (
    approvalId: string,
    actionHash: string,
    answer: AgentQuestionAnswer
  ) => Promise<unknown> | void
}

function repoName(url: string): string {
  return url
    .replace(/^https?:\/\/(www\.)?github\.com\//, '')
    .replace(/\.git$/, '')
}

/** One sentence saying what the agent wants to do. */
export function approvalHeadline(approval: Approval): string {
  const action = approval.action as ApprovalAction | undefined
  switch (action?.kind) {
    case 'branch_push':
      return `Push ${action.branch} to ${repoName(action.repositoryUrl)}`
    case 'pull_request_create_or_ready':
      return action.markReady
        ? `Mark pull request ready: ${action.title}`
        : `Open draft pull request: ${action.title}`
    case 'budget_or_time_exceeded':
      return `Continue past the ${action.limit.replace(/_/g, ' ')} limit`
    case 'privileged_or_destructive_tool':
      return `Run ${action.toolName}`
    case 'new_network_domain':
      return `Reach ${action.domain}`
    case 'follow_up_agent':
      return `Hand off to another agent: ${action.nextTaskTitle}`
    case 'persistent_memory_save':
      return 'Save to long-term memory (legacy)'
    case 'deployment_merge_or_release':
      return `${humanize(action.target)} ${action.ref} in ${repoName(action.repositoryUrl)}`
    case 'workflow_gate':
      return `Review “${humanize(action.state)}”`
    case 'agent_question':
      return 'The agent has a question for you'
    case 'agent_request':
      return action.action
    default:
      return humanize(approval.type)
  }
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3 py-1.5">
      <dt className="text-sm text-ink-3">{label}</dt>
      <dd className="min-w-0 break-words text-base text-ink">{children}</dd>
    </div>
  )
}

const Code = ({ children }: { children: ReactNode }) => (
  <code className="rounded bg-sunken px-1.5 py-0.5 font-mono text-sm">
    {children}
  </code>
)

function ActionPreview({ action }: { action: ApprovalAction }) {
  switch (action.kind) {
    case 'branch_push':
      return (
        <dl>
          <Detail label="Repository">{repoName(action.repositoryUrl)}</Detail>
          <Detail label="Branch">
            <Code>{action.branch}</Code>
          </Detail>
          <Detail label="Commit">
            <Code>{action.commit.slice(0, 12)}</Code>
          </Detail>
        </dl>
      )
    case 'pull_request_create_or_ready':
      return (
        <dl>
          <Detail label="Repository">{repoName(action.repositoryUrl)}</Detail>
          <Detail label="Branch">
            <Code>{action.branch}</Code>
          </Detail>
          <Detail label="Title">{action.title}</Detail>
          <Detail label="State">
            {action.markReady ? 'Ready for review' : 'Draft'}
          </Detail>
        </dl>
      )
    case 'budget_or_time_exceeded': {
      const ratio =
        action.limitValue > 0 ? action.currentValue / action.limitValue : 1
      const fmt = (v: number) =>
        action.limit === 'cost'
          ? formatMoney(v)
          : action.limit === 'duration'
            ? `${Math.round(v / 60000)} min`
            : v.toLocaleString()
      return (
        <div>
          <div className="flex items-baseline justify-between text-base">
            <span className="text-ink-2">{humanize(action.limit)} used</span>
            <span className="font-semibold text-ink">
              {fmt(action.currentValue)} of {fmt(action.limitValue)}
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-sunken">
            <div
              className="h-full rounded-full bg-signal"
              style={{ width: `${Math.min(100, ratio * 100)}%` }}
            />
          </div>
        </div>
      )
    }
    case 'privileged_or_destructive_tool':
      return (
        <dl>
          <Detail label="Tool">
            <Code>{action.toolName}</Code>
          </Detail>
          {Object.entries(action.params).map(([key, value]) => (
            <Detail key={key} label={key}>
              <Code>
                {typeof value === 'string' ? value : JSON.stringify(value)}
              </Code>
            </Detail>
          ))}
        </dl>
      )
    case 'new_network_domain':
      return (
        <dl>
          <Detail label="Domain">
            <Code>{action.domain}</Code>
          </Detail>
        </dl>
      )
    case 'follow_up_agent':
      return (
        <dl>
          <Detail label="Next task">{action.nextTaskTitle}</Detail>
        </dl>
      )
    case 'deployment_merge_or_release':
      return (
        <dl>
          <Detail label="Action">{humanize(action.target)}</Detail>
          <Detail label="Repository">{repoName(action.repositoryUrl)}</Detail>
          <Detail label="Ref">
            <Code>{action.ref}</Code>
          </Detail>
        </dl>
      )
    case 'workflow_gate':
      return (
        <dl>
          <Detail label="Workflow">{humanize(action.workflowKey)}</Detail>
          <Detail label="State">{humanize(action.state)}</Detail>
          <Detail label="Approve →">
            {action.onApprove ? humanize(action.onApprove) : 'Stays here'}
          </Detail>
          <Detail label="Reject →">
            {action.onReject ? humanize(action.onReject) : 'Stays here'}
          </Detail>
          {action.instructions ? (
            <Detail label="Check">
              <span className="whitespace-pre-wrap">{action.instructions}</span>
            </Detail>
          ) : null}
          {Object.keys(action.variables ?? {}).length ? (
            <Detail label="Workflow variables">
              <ul className="space-y-0.5">
                {Object.entries(action.variables ?? {}).map(([name, v]) => (
                  <li key={name}>
                    <Code>{name}</Code> = {v.value}
                    {v.reason ? <span className="text-ink-3"> ({v.reason})</span> : null}
                  </li>
                ))}
              </ul>
            </Detail>
          ) : null}
        </dl>
      )
    case 'agent_question':
      return (
        <dl>
          {action.questions && action.questions.length > 0 ? (
            <>
              {action.question ? (
                <Detail label="Intro">
                  <Markdown>{action.question}</Markdown>
                </Detail>
              ) : null}
              <Detail label="Questions">
                <ol className="list-decimal space-y-1 pl-4">
                  {action.questions.map((item, index) => (
                    <li key={item.id ?? index}>
                      <Markdown>{item.question}</Markdown>
                    </li>
                  ))}
                </ol>
              </Detail>
            </>
          ) : (
            <Detail label="Question">
              <Markdown>{action.question}</Markdown>
            </Detail>
          )}
          {action.context ? (
            <Detail label="Context">
              <span className="whitespace-pre-wrap">{action.context}</span>
            </Detail>
          ) : null}
        </dl>
      )
    case 'agent_request':
      return (
        <dl>
          <Detail label="Wants to">{action.action}</Detail>
          <Detail label="Reason">{action.reason}</Detail>
        </dl>
      )
    default:
      return (
        <pre className="max-h-64 overflow-auto rounded-control bg-sunken p-3 font-mono text-xs text-ink-2">
          {JSON.stringify(action, null, 2)}
        </pre>
      )
  }
}

/**
 * A `workflow_gate` approval's reviewer context: where the code is, what
 * changed, how it got here, and the last agent's handoff message — the
 * reviewer otherwise has nothing to go on but the bare state transition
 * `ActionPreview`'s `workflow_gate` case shows above this.
 */
function GateContextSection({
  context,
  taskId,
}: {
  context: WorkflowGateContext
  taskId: string | null
}) {
  const [showDiff, setShowDiff] = useState(false)
  return (
    <div className="space-y-5 border-t border-line px-6 py-5">
      {context.pullRequestUrl || context.branch || taskId ? (
        <div className="flex flex-wrap items-center gap-4 text-base">
          {context.pullRequestUrl ? (
            <a href={context.pullRequestUrl} target="_blank" rel="noreferrer" className="link font-medium">
              Open pull request
            </a>
          ) : null}
          {taskId ? <TaskReportLink taskId={taskId} /> : null}
          {context.branch ? (
            <span className="text-ink-2">
              Branch{' '}
              {context.branchUrl ? (
                <a href={context.branchUrl} target="_blank" rel="noreferrer" className="link font-mono text-sm">
                  {context.branch}
                </a>
              ) : (
                <Code>{context.branch}</Code>
              )}
            </span>
          ) : null}
        </div>
      ) : null}

      {context.diffstat ? (
        <div>
          <h3 className="mb-1.5 text-sm font-medium text-ink-3">What changed</h3>
          <p className="text-base text-ink">
            {context.diffstat.filesChanged} file{context.diffstat.filesChanged === 1 ? '' : 's'} changed,{' '}
            <span className="text-ok">+{context.diffstat.insertions}</span>{' '}
            <span className="text-danger">-{context.diffstat.deletions}</span>
          </p>
          <button type="button" className="mt-1 text-sm link" onClick={() => setShowDiff((v) => !v)}>
            {showDiff ? 'Hide files' : 'Show files'}
          </button>
          {showDiff ? (
            <ul className="mt-2 space-y-1 font-mono text-sm">
              {context.diffstat.files.map((file) => (
                <li key={file.path} className="flex items-center justify-between gap-3 text-ink-2">
                  <span className="truncate">{file.path}</span>
                  <span className="shrink-0">
                    <span className="text-ok">+{file.insertions}</span> <span className="text-danger">-{file.deletions}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {context.handoff ? (
        <div>
          <h3 className="mb-1.5 text-sm font-medium text-ink-3">
            Message from {context.handoff.agentName ?? context.handoff.roleName ?? 'the agent'}
          </h3>
          <div className="rounded-control border border-line bg-sunken/60 p-3 text-base text-ink">
            <Markdown>{context.handoff.summary}</Markdown>
          </div>
        </div>
      ) : null}

      {context.steps.length ? (
        <div>
          <h3 className="mb-1.5 text-sm font-medium text-ink-3">Steps so far</h3>
          <ol className="space-y-2 border-l border-line pl-4">
            {context.steps.map((step) =>
              step.kind === 'review' ? <ReviewStep key={step.runId} step={step} /> : <RunStep key={step.runId} step={step} />,
            )}
          </ol>
        </div>
      ) : null}
    </div>
  )
}

/** One run in "Steps so far": a header line, then its summary collapsed to a
 * single truncated line — click (or Enter/Space) to expand it in full,
 * rendered as Markdown like an agent's chat messages. Collapsed by default so
 * a long list of steps stays scannable. */
function RunStep({ step }: { step: TaskRunStep }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <li className="relative text-sm">
      <span aria-hidden className="absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-moss" />
      <p className="text-ink">
        {step.workflowState ? `${humanize(step.workflowState)}${step.round && step.round > 1 ? ` (round ${step.round})` : ''} · ` : ''}
        {step.agentName ?? step.roleName ?? 'Agent'} <StatusBadge status={step.status} />
        <span className="ml-1.5 text-ink-3"><LiveDuration startedAt={step.startedAt} endedAt={step.endedAt} /></span>
        <Link to="/runs/$runId" params={{ runId: step.runId }} className="ml-1.5 link">
          run
        </Link>
      </p>
      <StepChain step={step} />
      {step.summary ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 block w-full text-left text-ink-3 hover:text-ink"
        >
          {expanded ? <Markdown>{step.summary}</Markdown> : <span className="block truncate">{step.summary}</span>}
        </button>
      ) : null}
      {step.blockers.length ? <p className="mt-0.5 text-danger">{step.blockers.join('; ')}</p> : null}
    </li>
  )
}

/** A person's decision at a workflow gate, between the runs in "Steps so far". */
export function ReviewStep({ step }: { step: TaskRunStep }) {
  const approved = step.decision === 'approved'
  return (
    <li className="relative text-sm">
      <span
        aria-hidden
        className={`absolute -left-[21px] top-1 h-2 w-2 rounded-full ${approved ? 'bg-moss' : 'bg-danger'}`}
      />
      <p className="text-ink">
        {step.workflowState ? `${humanize(step.workflowState)} · ` : ''}
        {step.reviewerName ?? 'Reviewer'}{' '}
        <span className={approved ? 'text-moss' : 'text-danger'}>{approved ? 'Approved' : 'Sent back'}</span>
      </p>
      {step.note ? <p className="mt-0.5 whitespace-pre-wrap text-ink-3">“{step.note}”</p> : null}
    </li>
  )
}

interface MultiQuestionAnswerState {
  text: string
  choices: string[]
  other: string
}

function emptyMultiAnswer(): MultiQuestionAnswerState {
  return { text: '', choices: [], other: '' }
}

function isMultiAnswerValid(item: AgentQuestionItem, state: MultiQuestionAnswerState): boolean {
  const kind = item.askKind ?? 'text'
  if (kind === 'yes_no') return state.text === 'Yes' || state.text === 'No'
  if (kind === 'single_choice' || kind === 'multiple_choice') {
    return state.choices.length > 0 || Boolean(item.allowOther && state.other.trim())
  }
  return state.text.trim().length > 0
}

/**
 * One ask_human call's several sub-questions (`AgentQuestionAction.
 * questions`), rendered as a single numbered form with one "Answer" button
 * — each sub-question gets the same control its `askKind` would get alone
 * (textarea / Yes-No buttons / radios or checkboxes + optional "Other"), but
 * nothing submits until every sub-question holds a valid answer.
 */
function MultiQuestionForm({
  approvalId,
  questions,
  isSubmitting,
  isExpired,
  onSubmit,
}: {
  approvalId: string
  questions: AgentQuestionItem[]
  isSubmitting?: boolean
  isExpired: boolean
  onSubmit: (answers: AgentQuestionAnswerItem[]) => void
}) {
  const [answers, setAnswers] = useState<MultiQuestionAnswerState[]>(() => questions.map(() => emptyMultiAnswer()))

  function update(index: number, patch: Partial<MultiQuestionAnswerState>): void {
    setAnswers((prev) => prev.map((state, i) => (i === index ? { ...state, ...patch } : state)))
  }

  function toggleChoice(index: number, option: string, kind: 'single_choice' | 'multiple_choice'): void {
    setAnswers((prev) =>
      prev.map((state, i) => {
        if (i !== index) return state
        if (kind === 'single_choice') return { ...state, choices: [option] }
        return {
          ...state,
          choices: state.choices.includes(option) ? state.choices.filter((o) => o !== option) : [...state.choices, option],
        }
      }),
    )
  }

  const allValid = questions.every((item, index) => isMultiAnswerValid(item, answers[index] ?? emptyMultiAnswer()))

  function handleSubmit(): void {
    const payload: AgentQuestionAnswerItem[] = questions.map((item, index) => {
      const state = answers[index] ?? emptyMultiAnswer()
      const kind = item.askKind ?? 'text'
      const base = { ...(item.id ? { id: item.id } : {}), index }
      if (kind === 'single_choice' || kind === 'multiple_choice') {
        const other = state.other.trim()
        return { ...base, choices: state.choices, ...(other ? { other } : {}) }
      }
      return { ...base, answer: state.text.trim() }
    })
    onSubmit(payload)
  }

  return (
    <div className="space-y-5">
      {questions.map((item, index) => {
        const kind = item.askKind ?? 'text'
        const state = answers[index] ?? emptyMultiAnswer()
        return (
          <div key={item.id ?? index}>
            <p className="mb-1.5 text-base font-medium text-ink">
              {index + 1}. <Markdown>{item.question}</Markdown>
            </p>
            {kind === 'yes_no' ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={`btn-secondary ${state.text === 'Yes' ? 'border-action bg-action/5 text-action' : ''}`}
                  onClick={() => update(index, { text: 'Yes' })}
                  disabled={isSubmitting || isExpired}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`btn-secondary ${state.text === 'No' ? 'border-action bg-action/5 text-action' : ''}`}
                  onClick={() => update(index, { text: 'No' })}
                  disabled={isSubmitting || isExpired}
                >
                  No
                </button>
              </div>
            ) : kind === 'single_choice' || kind === 'multiple_choice' ? (
              <>
                <div className="space-y-2">
                  {(item.options ?? []).map((option) => {
                    const selected = state.choices.includes(option)
                    return (
                      <label
                        key={option}
                        className={`flex cursor-pointer gap-3 rounded-control border px-4 py-2.5 transition-colors ${
                          selected ? 'border-action bg-action/5' : 'border-line hover:border-line-strong'
                        }`}
                      >
                        <input
                          type={kind === 'single_choice' ? 'radio' : 'checkbox'}
                          name={`answer-${approvalId}-${index}`}
                          className="mt-1"
                          checked={selected}
                          onChange={() => toggleChoice(index, option, kind)}
                          disabled={isSubmitting || isExpired}
                        />
                        <span className="text-base text-ink">{option}</span>
                      </label>
                    )
                  })}
                </div>
                {item.allowOther ? (
                  <div className="mt-2">
                    <input
                      className="field-input"
                      value={state.other}
                      onChange={(event) => update(index, { other: event.target.value })}
                      placeholder="Other…"
                      disabled={isSubmitting || isExpired}
                    />
                  </div>
                ) : null}
              </>
            ) : (
              <textarea
                className="field-input"
                rows={2}
                value={state.text}
                onChange={(event) => update(index, { text: event.target.value })}
                placeholder="Your answer…"
                disabled={isSubmitting || isExpired}
              />
            )}
          </div>
        )
      })}
      <div className="flex items-center gap-2">
        <button type="button" className="btn-primary" onClick={handleSubmit} disabled={isSubmitting || isExpired || !allValid}>
          Answer
        </button>
      </div>
    </div>
  )
}

export function ApprovalPanel({
  approval,
  isSubmitting,
  agentName = (id) => id,
  projectName = (id) => id,
  taskName = (id) => id,
  showRunLink = true,
  onApprove,
  onReject,
  onInstruct,
  onAnswer,
}: ApprovalPanelProps) {
  const [note, setNote] = useState('')
  const [mode, setMode] = useState<'decide' | 'instruct'>('decide')
  const [instruction, setInstruction] = useState('')
  const [answer, setAnswer] = useState('')
  const [selectedChoices, setSelectedChoices] = useState<string[]>([])
  const [otherText, setOtherText] = useState('')
  const [staleWarning, setStaleWarning] = useState(false)
  const isQuestion = approval.type === 'agent_question'
  const questionAction = isQuestion ? (approval.action as AgentQuestionAction) : undefined
  const askKind = questionAction?.askKind ?? 'text'

  const expiresInMs = new Date(approval.expiresAt).getTime() - Date.now()
  const isExpired = expiresInMs <= 0

  async function verifyStillCurrent(): Promise<boolean> {
    const unchanged = await isApprovalActionUnchanged(
      approval.action,
      approval.actionHash
    )
    setStaleWarning(!unchanged)
    return unchanged
  }

  async function handleApprove(): Promise<void> {
    if (!(await verifyStillCurrent())) return
    await onApprove(approval.id, approval.actionHash, note.trim() || undefined)
  }

  async function handleReject(): Promise<void> {
    if (!(await verifyStillCurrent())) return
    await onReject(approval.id, approval.actionHash, note.trim() || undefined)
  }

  async function handleInstruct(): Promise<void> {
    if (!instruction.trim()) return
    if (!(await verifyStillCurrent())) return
    await onInstruct(approval.id, approval.actionHash, instruction.trim())
    setInstruction('')
    setMode('decide')
  }

  const [comment, setComment] = useState('')

  async function submitAnswer(payload: AgentQuestionAnswer): Promise<void> {
    if (!onAnswer) return
    if (!(await verifyStillCurrent())) return
    const extra = comment.trim()
    await onAnswer(approval.id, approval.actionHash, extra ? { ...payload, comment: extra } : payload)
    setComment('')
    setAnswer('')
    setSelectedChoices([])
    setOtherText('')
  }

  function toggleChoice(option: string): void {
    setSelectedChoices((prev) =>
      askKind === 'multiple_choice' ? (prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]) : [option],
    )
  }

  const trimmedOther = otherText.trim()
  const canSubmitChoice = selectedChoices.length > 0 || Boolean(questionAction?.allowOther && trimmedOther)

  return (
    <article
      className="overflow-hidden rounded-panel border border-signal/40 bg-surface"
      data-approval-id={approval.id}
      data-risk={approval.riskIndicator}
    >
      <header className="flex items-start gap-5 border-b border-line px-6 py-5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-3">
            <span className="font-medium text-signal">
              {humanize(approval.type)}
            </span>
            <StatusBadge status={approval.riskIndicator} />
            <span className={isExpired ? 'font-medium text-danger' : ''}>
              {isExpired
                ? 'Expired'
                : `Expires ${formatRelative(approval.expiresAt)}`}
            </span>
          </div>
          <h2 className="mt-2 font-display text-lg font-semibold text-ink">
            {approvalHeadline(approval)}
          </h2>
          {approval.reason && approval.type !== 'agent_question' ? (
            <p className="mt-1.5 max-w-prose text-base text-ink-2">
              {approval.reason}
            </p>
          ) : null}
        </div>
        <MomentArt
          moment="waiting"
          size={88}
          className="-my-2 hidden sm:block"
        />
      </header>

      <div className="grid grid-cols-1 gap-6 px-6 py-5 md:grid-cols-[minmax(0,1fr)_220px]">
        <div>
          <h3 className="mb-2 text-sm font-medium text-ink-3">
            What will happen
          </h3>
          <ActionPreview action={approval.action} />
        </div>
        <dl className="space-y-3 text-base md:border-l md:border-line md:pl-6">
          <div>
            <dt className="text-sm text-ink-3">Agent</dt>
            <dd className="text-ink">
              {approval.agentId ? agentName(approval.agentId) : 'Workflow'}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-ink-3">Project</dt>
            <dd className="text-ink">{projectName(approval.projectId)}</dd>
          </div>
          {approval.taskId ? (
            <div>
              <dt className="text-sm text-ink-3">Task</dt>
              <dd className="text-ink">{taskName(approval.taskId)}</dd>
              {approval.type !== 'workflow_gate' ? (
                <dd>
                  <TaskReportLink
                    taskId={approval.taskId}
                    className="link text-sm"
                  />
                </dd>
              ) : null}
            </div>
          ) : null}
          <div>
            <dt className="text-sm text-ink-3">Expected cost</dt>
            <dd className="text-ink">
              {formatMoney(approval.expectedCostCents)}
            </dd>
          </div>
          {showRunLink && approval.runId ? (
            <div>
              <Link
                to="/runs/$runId"
                params={{ runId: approval.runId }}
                className="text-sm link"
              >
                Open run
              </Link>
            </div>
          ) : null}
        </dl>
      </div>

      {approval.type === 'workflow_gate' && approval.gateContext ? (
        <GateContextSection
          context={approval.gateContext}
          taskId={approval.taskId ?? null}
        />
      ) : null}

      {staleWarning ? (
        <p
          className="mx-6 mb-4 rounded-control border border-danger/30 bg-danger-soft px-3 py-2 text-base text-ink"
          role="alert"
        >
          The agent changed this action after asking. Reload before deciding.
        </p>
      ) : null}

      <footer className="border-t border-line bg-sunken/50 px-6 py-4">
        {isQuestion && !isExpired ? (
          <div className="mb-4">
            <label htmlFor={`comment-${approval.id}`} className="mb-1.5 block text-sm text-ink-3">
              Comment (optional)
            </label>
            <textarea
              id={`comment-${approval.id}`}
              className="field-input min-h-16"
              placeholder="Extra context for the agent."
              maxLength={2000}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              disabled={isSubmitting}
            />
          </div>
        ) : null}
        {isQuestion && questionAction?.questions && questionAction.questions.length > 0 ? (
          <MultiQuestionForm
            approvalId={approval.id}
            questions={questionAction.questions}
            isSubmitting={isSubmitting}
            isExpired={isExpired}
            onSubmit={(answers) => submitAnswer({ answers })}
          />
        ) : isQuestion && askKind === 'yes_no' ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary"
              onClick={() => submitAnswer({ answer: 'Yes' })}
              disabled={isSubmitting || isExpired}
            >
              Yes
            </button>
            <button
              type="button"
              className="btn-danger-quiet"
              onClick={() => submitAnswer({ answer: 'No' })}
              disabled={isSubmitting || isExpired}
            >
              No
            </button>
          </div>
        ) : isQuestion && (askKind === 'single_choice' || askKind === 'multiple_choice') ? (
          <>
            <div className="space-y-2">
              {(questionAction?.options ?? []).map((option) => {
                const selected = selectedChoices.includes(option)
                return (
                  <label
                    key={option}
                    className={`flex cursor-pointer gap-3 rounded-control border px-4 py-2.5 transition-colors ${
                      selected ? 'border-action bg-action/5' : 'border-line hover:border-line-strong'
                    }`}
                  >
                    <input
                      type={askKind === 'single_choice' ? 'radio' : 'checkbox'}
                      name={`answer-${approval.id}`}
                      className="mt-1"
                      checked={selected}
                      onChange={() => toggleChoice(option)}
                      disabled={isSubmitting || isExpired}
                    />
                    <span className="text-base text-ink">{option}</span>
                  </label>
                )
              })}
            </div>
            {questionAction?.allowOther ? (
              <div className="mt-3">
                <label htmlFor={`other-${approval.id}`} className="mb-1.5 block text-sm text-ink-3">
                  Other
                </label>
                <input
                  id={`other-${approval.id}`}
                  className="field-input"
                  value={otherText}
                  onChange={(event) => setOtherText(event.target.value)}
                  placeholder="Something else…"
                  disabled={isSubmitting || isExpired}
                />
              </div>
            ) : null}
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                className="btn-primary"
                onClick={() => submitAnswer({ choices: selectedChoices, other: trimmedOther || undefined })}
                disabled={isSubmitting || isExpired || !canSubmitChoice}
              >
                Answer
              </button>
            </div>
          </>
        ) : isQuestion ? (
          <>
            <label
              htmlFor={`answer-${approval.id}`}
              className="mb-1.5 block text-sm text-ink-3"
            >
              Your answer
            </label>
            <textarea
              id={`answer-${approval.id}`}
              className="field-input"
              rows={3}
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              placeholder="Answer the agent's question…"
              disabled={isSubmitting || isExpired}
              autoFocus
            />
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                className="btn-primary"
                onClick={() => submitAnswer({ answer: answer.trim() })}
                disabled={isSubmitting || isExpired || !answer.trim()}
              >
                Answer
              </button>
            </div>
          </>
        ) : mode === 'decide' ? (
          <>
            <label
              htmlFor={`note-${approval.id}`}
              className="mb-1.5 block text-sm text-ink-3"
            >
              Note for the audit log (optional)
            </label>
            <textarea
              id={`note-${approval.id}`}
              className="field-input"
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={isSubmitting || isExpired}
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn-primary"
                onClick={handleApprove}
                disabled={isSubmitting || isExpired}
              >
                Approve
              </button>
              <button
                type="button"
                className="btn-danger-quiet"
                onClick={handleReject}
                disabled={isSubmitting || isExpired}
              >
                Reject
              </button>
              {/* A workflow gate has no run to pass an instruction to. */}
              {approval.type !== 'workflow_gate' ? (
                <button
                  type="button"
                  className="btn-ghost ml-auto"
                  onClick={() => setMode('instruct')}
                  disabled={isSubmitting || isExpired}
                >
                  Reply with an instruction
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <label
              htmlFor={`instruct-${approval.id}`}
              className="mb-1.5 block text-sm text-ink-3"
            >
              Tell the agent what to change. It keeps waiting until you approve
              or reject.
            </label>
            <textarea
              id={`instruct-${approval.id}`}
              className="field-input"
              rows={3}
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              placeholder="e.g. Squash the commits first and use a conventional commit title."
              disabled={isSubmitting}
              autoFocus
            />
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                className="btn-primary"
                onClick={handleInstruct}
                disabled={isSubmitting || !instruction.trim()}
              >
                Send instruction
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setMode('decide')}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </footer>
    </article>
  )
}
